import React, { useEffect, useState } from "react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";

function AccordionItem({ id, title, children, isOpen, onToggle }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white">
      <button
        aria-expanded={isOpen}
        aria-controls={`panel-${id}`}
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-4 px-4 py-4 text-left"
      >
        <span className="text-[15px] font-medium text-slate-800">{title}</span>
        <ChevronDownIcon
          className={`h-5 w-5 text-slate-500 transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>
      <div
        id={`panel-${id}`}
        role="region"
        aria-labelledby={`btn-${id}`}
        className={`px-4 pb-5 text-sm leading-6 text-slate-700 ${isOpen ? "block" : "hidden"}`}
      >
        {children}
      </div>
    </div>
  );
}

// ---------------- PRIVACY POLICY PAGE ----------------
export default function PrivacyPolicyPage() {
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    const fromHash = window.location.hash?.replace("#", "");
    if (fromHash) setOpenId(fromHash);
  }, []);

  return (
    <main className="min-h-screen bg-slate-50">
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 py-8">
          <h1 className="text-2xl font-semibold text-slate-900">Privacy Policy</h1>
          <p className="mt-1 text-sm text-slate-600">Last updated on Nov 25 2025</p>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 py-8 space-y-3">
        <AccordionItem
          id="intro"
          title="Introduction"
          isOpen={openId === null || openId === "intro"}
          onToggle={() => setOpenId(openId === "intro" ? null : "intro")}
        >
          <p>
            This Privacy Policy explains how ShowTimeBro collects, uses, stores, and protects your information when you use our platform and services.
          </p>
        </AccordionItem>

        <AccordionItem
          id="data-collection"
          title="Information We Collect"
          isOpen={openId === "data-collection"}
          onToggle={() => setOpenId(openId === "data-collection" ? null : "data-collection")}
        >
          <ul className="list-disc pl-6 space-y-1">
            <li>Personal information (name, email, phone number)</li>
            <li>Payment details processed securely through payment gateways</li>
            <li>Usage data such as device info, IP address, location</li>
            <li>Event booking history and activity</li>
          </ul>
        </AccordionItem>

        <AccordionItem
          id="use-of-data"
          title="How We Use Your Information"
          isOpen={openId === "use-of-data"}
          onToggle={() => setOpenId(openId === "use-of-data" ? null : "use-of-data")}
        >
          <ul className="list-disc pl-6 space-y-1">
            <li>To process ticket bookings and payments</li>
            <li>To provide customer support</li>
            <li>To improve platform performance and user experience</li>
            <li>To send booking confirmations and service updates</li>
          </ul>
        </AccordionItem>

        <AccordionItem
          id="sharing"
          title="Sharing of Information"
          isOpen={openId === "sharing"}
          onToggle={() => setOpenId(openId === "sharing" ? null : "sharing")}
        >
          <p>We do not sell or trade your personal information. We may share your data only with:</p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>Event organisers (only necessary details)</li>
            <li>Payment gateways for transaction processing</li>
            <li>Legal authorities (only when required by law)</li>
          </ul>
        </AccordionItem>

        <AccordionItem
          id="security"
          title="Data Security"
          isOpen={openId === "security"}
          onToggle={() => setOpenId(openId === "security" ? null : "security")}
        >
          <p>
            We use industry‑standard security measures to protect your information from unauthorized access, loss, or misuse.
          </p>
        </AccordionItem>

        <AccordionItem
          id="rights"
          title="Your Rights"
          isOpen={openId === "rights"}
          onToggle={() => setOpenId(openId === "rights" ? null : "rights")}
        >
          <ul className="list-disc pl-6 space-y-1">
            <li>Request access to your data</li>
            <li>Request correction or deletion of your information</li>
            <li>Withdraw consent for communications</li>
          </ul>
        </AccordionItem>

        <AccordionItem
          id="contact"
          title="Contact & Grievance"
          isOpen={openId === "contact"}
          onToggle={() => setOpenId(openId === "contact" ? null : "contact")}
        >
          <p>If you have privacy concerns, contact:</p>
          <ul className="list-disc pl-6 mt-2">
            <li><strong>Email:</strong> info@showtimebro.com</li>
            <li><strong>Phone:</strong> 7440550105</li>
          </ul>
        </AccordionItem>
      </div>
    </main>
  );
}
